import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Phone, ArrowRight, Home as HomeIcon, FileDown, Truck, ShieldCheck, Lock, Unlock, Info } from "lucide-react";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StickyCall } from "../components/StickyCall";
import { Marquee } from "../components/Marquee";
import { SquareCheckout } from "../components/SquareCheckout";
import { Reveal, StaggerLines } from "../components/Reveal";
import { PASSPORT_PRODUCTS, PASSPORT_PRODUCT_LIST } from "../data/passportProducts";
import { PHONE, PHONE_HREF, PHONE2, IMAGES } from "../lib/site";

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

export default function ProductPage() {
  const { slug } = useParams();
  const p = PASSPORT_PRODUCTS[slug];
  if (!p) return <Navigate to="/" replace />;

  const { step1, step2 } = p;
  const others = PASSPORT_PRODUCT_LIST.filter((x) => x.slug !== slug);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: p.h1,
    description: p.metaDescription,
    provider: { "@type": "LocalBusiness", name: "Urgent Passport & Visa", telephone: "+1-866-334-8472" },
    areaServed: ["Washington DC", "Maryland", "Virginia", "United States"],
    url: `https://upvservices.com/product/${slug}`,
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://upvservices.com/" },
      { "@type": "ListItem", position: 2, name: p.h1, item: `https://upvservices.com/product/${slug}` },
    ],
  };

  const ShipBlock = ({ ship }) => (
    <div className="rounded-2xl bg-[#0b192c] text-white p-6" data-testid="product-ship">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Truck className="w-5 h-5 text-white" /></span>
        <div>
          <h3 className="font-display font-bold text-lg">Ship Materials to UPV Services</h3>
          <p className="mt-1 text-sm text-white/60">Shipping Method: {ship.method}</p>
          <address className="mt-3 not-italic text-[15px] leading-relaxed text-white/90">
            {ship.company}<br />
            {ship.recipient}<br />
            {ship.address}<br />
            {ship.phone}
          </address>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="product-page">
      <Seo title={p.metaTitle} description={p.metaDescription} path={`/product/${slug}`} jsonLd={[serviceSchema, breadcrumb]} />
      <Header />
      <main id="main-content" tabIndex={-1}>
        {/* Hero */}
        <section className="relative bg-[#0b192c] text-white overflow-hidden" data-testid="product-hero">
          <div className="noise-overlay" />
          <div className="absolute inset-0">
            <img src={IMAGES.passport} alt={p.h1} className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0b192c] via-[#0b192c]/90 to-[#0b192c]/40" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-20 md:pt-24 md:pb-28">
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-8" aria-label="Breadcrumb">
              <Link to="/" onClick={scrollTop} className="hover:text-white inline-flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" /> Home</Link>
              <span>/</span>
              <span className="text-white/90">{p.overline}</span>
            </nav>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#ef233c]">{p.overline}</span>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] tracking-tight max-w-3xl">
              <StaggerLines lines={[p.h1]} />
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }} className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed">
              {p.intro}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.6 }} className="mt-8 flex flex-wrap gap-4">
              <a href="#quote" className="inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-7 py-4 font-semibold hover:bg-[#ef233c] transition-colors" data-testid="product-cta-quote">
                Start My Application <ArrowRight className="w-4 h-4" />
              </a>
              <a href={PHONE_HREF} className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 text-white px-7 py-[14px] font-semibold hover:bg-white hover:text-[#0b192c] transition-colors" data-testid="product-cta-call">
                <Phone className="w-4 h-4" /> Call for a Quote: {PHONE}
              </a>
            </motion.div>
          </div>
        </section>

        <Marquee />

        {/* Steps + form */}
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 space-y-14">
              {/* Step 1 */}
              <Reveal>
                <div data-testid="product-step1">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-[#d90429] text-white flex items-center justify-center font-display font-bold">1</span>
                    <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0b192c] tracking-tight">{step1.heading.replace(/^Step 1:\s*/, "")}</h2>
                  </div>
                  <ol className="mt-6 space-y-4">
                    {step1.items.map((it, i) => (
                      <li key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_2px_16px_rgba(11,25,44,0.04)]" data-testid={`product-step1-item-${i}`}>
                        <div className="flex items-start gap-3">
                          <span className="shrink-0 w-6 h-6 rounded-full bg-[#d90429]/10 flex items-center justify-center mt-0.5"><Check className="w-3.5 h-3.5 text-[#d90429]" /></span>
                          <div className="min-w-0">
                            <p className="text-[16px] font-semibold text-[#0b192c]">{it.title}</p>
                            <p className="mt-1 text-[15px] text-slate-600 leading-relaxed">{it.desc}</p>
                            {it.form && (
                              <a href={it.form.url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-[#0b192c] hover:border-[#d90429] hover:text-[#d90429] transition-colors" data-testid={`product-form-${i}`}>
                                <FileDown className="w-4 h-4 text-[#d90429]" /> {it.form.name}
                              </a>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                  {step1.fee && (
                    <div className="mt-5 rounded-xl border border-[#d90429]/20 bg-[#d90429]/5 px-4 py-3.5 flex items-start gap-3" data-testid="product-fee">
                      <ShieldCheck className="w-5 h-5 text-[#d90429] shrink-0 mt-0.5" />
                      <p className="text-[15px] text-[#0b192c] font-medium leading-relaxed">{step1.fee}</p>
                    </div>
                  )}
                </div>
              </Reveal>

              {/* Step 2 */}
              <Reveal delay={0.05}>
                <div data-testid="product-step2">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-[#d90429] text-white flex items-center justify-center font-display font-bold">2</span>
                    <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0b192c] tracking-tight">{step2.heading.replace(/^Step 2:\s*/, "")}</h2>
                  </div>

                  {step2.authorize && (
                    <div className="mt-6 space-y-5">
                      <p className="text-[15px] text-slate-600 leading-relaxed">{step2.authorize.intro}</p>
                      <a href="https://iafdb.travel.state.gov/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#0b192c] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#1a2a40] transition-colors" data-testid="product-find-aa">
                        Find your nearest Acceptance Agent <ArrowRight className="w-4 h-4" />
                      </a>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-slate-200 bg-[#f8f9fa] p-5">
                          <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-[#d90429]" /><h3 className="font-display font-bold text-[#0b192c]">Sealed by the AA</h3></div>
                          <ul className="mt-3 space-y-2">
                            {step2.authorize.sealed.map((s, i) => (
                              <li key={i} className="flex items-start gap-2 text-[14px] text-slate-700"><span className="text-[#d90429] mt-0.5">•</span> {s}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-[#f8f9fa] p-5">
                          <div className="flex items-center gap-2"><Unlock className="w-4 h-4 text-[#d90429]" /><h3 className="font-display font-bold text-[#0b192c]">Left unsealed (loose)</h3></div>
                          <ul className="mt-3 space-y-2">
                            {step2.authorize.unsealed.map((s, i) => (
                              <li key={i} className="flex items-start gap-2 text-[14px] text-slate-700"><span className="text-[#d90429] mt-0.5">•</span> {s}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-slate-200 p-5">
                        <div className="flex items-center gap-2"><Info className="w-4 h-4 text-[#d90429]" /><h3 className="font-display font-bold text-[#0b192c]">Important notes</h3></div>
                        <ul className="mt-3 space-y-2.5">
                          {step2.authorize.notes.map((n, i) => (
                            <li key={i} className="flex items-start gap-2 text-[14px] text-slate-600 leading-relaxed"><span className="text-[#d90429] mt-0.5">•</span> {n}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {!step2.authorize && <div className="mt-6"><ShipBlock ship={step2.ship} /></div>}
                </div>
              </Reveal>

              {/* Step 3 (ship) — only when Step 2 was authorization */}
              {step2.authorize && (
                <Reveal delay={0.05}>
                  <div data-testid="product-step3">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="w-9 h-9 rounded-full bg-[#d90429] text-white flex items-center justify-center font-display font-bold">3</span>
                      <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0b192c] tracking-tight">Ship Your Application Package</h2>
                    </div>
                    <ShipBlock ship={step2.ship} />
                  </div>
                </Reveal>
              )}
            </div>

            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-24 space-y-6" id="quote">
                <SquareCheckout
                  title={p.h1}
                  category="passport"
                  service={slug}
                  defaults={{ shipping_id: "none" }}
                  fields={[
                    { key: "speed", label: "Processing Speed", required: true, options: [
                      { value: "emergency", label: "Emergency Service (5–7 business days) — $349" },
                      { value: "urgent", label: "Urgent Service (10–12 business days) — $249" },
                      { value: "rush", label: "Rush Service (15–20 business days) — $149" },
                      { value: "regular", label: "Regular Service (4–6 weeks) — $99" },
                    ] },
                    ...(["Renew-My-Passport", "Second-Passport", "Passport-Name-Change"].includes(slug)
                      ? [{ key: "passport_card", type: "checkbox", label: "Add passport card (+$30)" }] : []),
                    { key: "shipping_id", label: "Return Shipping", options: [
                      { value: "none", label: "No return label needed" },
                      { value: "own", label: "Include your own label" },
                      { value: "pickup", label: "Pick up from DC office" },
                      { value: "fedex_wd", label: "FedEx overnight (weekdays) (+$45.00)" },
                      { value: "fedex", label: "FedEx overnight (+$65.00)" },
                    ] },
                  ]}
                />
                <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="product-other-services">
                  <h3 className="font-display font-bold text-[#0b192c]">Other passport services</h3>
                  <div className="mt-3 flex flex-col">
                    {others.map((o) => (
                      <Link key={o.slug} to={`/product/${o.slug}`} onClick={scrollTop} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0 text-[15px] text-[#0b192c] hover:text-[#d90429] transition-colors" data-testid={`other-${o.slug}`}>
                        {o.label} <ArrowRight className="w-4 h-4" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}
